// hooks/useCarPaymentDetails.ts
import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Order, CarDetailState, TimeState } from "../../types";
import { fetchOrderData, fetchCategories, confirmOrder, updateCustomer } from "../services/carWashApi";

// ✅ Utility: Current time generator
const getCurrentTime = (): TimeState => {
  const d = new Date();
  return {
    year: String(d.getFullYear()),
    month: String(d.getMonth() + 1).padStart(2, "0"),
    day: String(d.getDate()).padStart(2, "0"),
    hours: String(d.getHours()).padStart(2, "0"),
    minutes: String(d.getMinutes()).padStart(2, "0"),
    seconds: String(d.getSeconds()).padStart(2, "0"),
  };
};

// ✅ Hook: use live clock
const useCurrentTime = () => {
  const [time, setTime] = useState<TimeState>(getCurrentTime());
  useEffect(() => {
    const interval = setInterval(() => setTime(getCurrentTime()), 1000);
    return () => clearInterval(interval);
  }, []);
  return time;
};

// ✅ Main Hook
export const useCarPaymentDetails = () => {
  const [orderData, setOrderData] = useState<Order | null>(null);
  const [categories, setCategories] = useState<CarDetailState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");

  const time = useCurrentTime();

  const fetchData = useCallback(async () => {
    if (!id) {
      setError("Invalid order ID.");
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const [order, cats] = await Promise.all([fetchOrderData(id), fetchCategories()]);
      setOrderData(order);
      setCategories(cats);
      setError(null);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    orderData,
    categories,
    error,
    isLoading,
    time,
    id,
    confirmOrder,
    updateCustomer: (data: any) => (id ? updateCustomer(id, data) : Promise.reject("Invalid ID")),
    refetch: fetchData,
  };
};
